import { Component, OnInit, ViewChild } from '@angular/core';

import { TranslateParser, TranslateService } from '@ngx-translate/core';

import { ToastsManager } from "@app/shared/util/toastr.service";

import { LineService } from '../../../../core/services/line.service';
import { Line } from '../../../../shared/models/line.model';

import { TableConfig } from '../../../../shared/util/table-config.utils';

import { LineCreateModalComponent } from './components/line-create-modal/line-create-modal.component';
import { TableBasedComponent } from "@app/shared/interface/TableBasedComponent";
import { Log } from "ng2-logger";
import { PermissionService } from "@app/authentication/services/permission.service";
import { AppUtils } from "@app/shared/util/app.utils";
import * as FileSaver from "file-saver";
import {HttpResponse} from "@angular/common/http";

@Component({
    selector: 'app-line-management',
    templateUrl: './lines.component.html',
    styleUrls: ['./lines.component.scss']
})
export class LinesComponent implements OnInit, TableBasedComponent<Line> {
    _logger = Log.create(LinesComponent.name);
    @ViewChild(LineCreateModalComponent, {static: true}) lineFormModal: LineCreateModalComponent;

    lines: Line[] = [];
    tableConfig = new TableConfig();
    isLoadingLines = false;

    constructor(private lineService: LineService,
                private toastr: ToastsManager,
                private translationService: TranslateService,
                private permissionService: PermissionService,
                private translateParser: TranslateParser) {

    }

    ngOnInit() {
        this.getAll(this.tableConfig);
    }

    openLineModal() {
        this.lineFormModal.open();
    }

    onTableRowClick(line: Line) {
        this.lineFormModal.open(line);
    }

    onDuplicateRow(line) {
        this._logger.info('onDuplicateRow::', line);
        line.name = line.name + AppUtils.COPY_STRING;
        line.lineLocations = [{id: null, name: ""}];
        this.lineFormModal.open(line, true);
    }

    getAll(tableConfig: TableConfig = null) {
        this.isLoadingLines = true;
        this.lineService.getAllLines(tableConfig).subscribe(
            (data: any) => {
                this.lines = data.items;
                this.tableConfig.pager.count = data.total;
                this.isLoadingLines = false;
            },
            (error: any) => {
                this.isLoadingLines = false;
                this._logger.error(error);
            }
        );
    }

    create(line: Line) {
        this._logger.info("create:: Creating line ", line);
        this.isLoadingLines = true;
        this.lineService.createLine(line).subscribe(
            (data: any) => {
                this._logger.info("create:: API returned data", data);
                this.getAll(this.tableConfig);
                this.isLoadingLines = false;
                this.toastr.success(this.buildCreateMessage(data.name));
                this.lineFormModal.close();
            },
            (error: any) => {
                this._logger.info("create:: API Return error", error);
                this.isLoadingLines = false;
            }
        );
    }

    remove(line) {
        this.isLoadingLines = true;
        this.lineService.updateLine({...line, isDeleted: true}).subscribe(
            (data: any) => {
                this.getAll();
                this.toastr.warning(this.buildDeleteMessage(line.name));
                this.isLoadingLines = false;
                this.lineFormModal.close();
            },
            (error: any) => {
                this._logger.error(error);
                this.isLoadingLines = false;
            }
        );
    }

    update(lineUpdate) {
        this._logger.info("update:: Updating line ", lineUpdate);
        this.isLoadingLines = true;
        this.lineService.updateLine(lineUpdate.newLine).subscribe(
            (data: any) => {
                this._logger.info("update:: API returned data", data);
                this.getAll(this.tableConfig);
                this.toastr.success(this.buildUpdateMessage(lineUpdate.previousLineName));
                this.isLoadingLines = false;
                this.lineFormModal.close();
            },
            (error: any) => {
                this._logger.error("update:: API returned error", error);
                this.isLoadingLines = false;
            }
        );
    }

    onSortChange(tableState: TableConfig) {
        this.getAll(tableState);
    }

    onFilter(tableConfig: TableConfig) {
        this.getAll(tableConfig);
    }

    onPageChange(tableConfig: TableConfig) {
        this.getAll(tableConfig);
    }

    buildCreateMessage(lineName) {
        const message = this.translationService.instant('admin.lines.toasts.created');
        return this.translateParser.interpolate(message, {lineName: lineName});
    }

    buildUpdateMessage(lineName) {
        const message = this.translationService.instant('admin.lines.toasts.updated');
        return this.translateParser.interpolate(message, {lineName: lineName});
    }

    buildDeleteMessage(lineName) {
        const message = this.translationService.instant('admin.lines.toasts.deleted');
        return this.translateParser.interpolate(message, {lineName: lineName});
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_LINES');
    }

    onDownloadLineReport(tableConfig = this.tableConfig) {
        this.lineService.downloadLinesReport(tableConfig).subscribe(
            (data: HttpResponse<any>) => {
                if (data.body != null) {
                    this._logger.info('onDownloadLineReport:: API Returned data', data);
                    const blob = new Blob([data.body], {type: 'application/xsls'});
                    const filename = data.headers.get('x-filename');
                    FileSaver.saveAs(blob, filename);
                }
            },
            (error: any) => {
                this._logger.error('onDownloadLineReport:: API Returned error', error);
            });
    }
}


